// Third-party
import React from 'react';
import ReactDOMServer from 'react-dom/server';

// Namespace
import * as ProposalNS from '../Proposal';

// Utils
import { formatLocalizedString, formatMessageWithLocale } from '../../../utils/i18n';
import { getRiskProfileRuleData, getRiskProfileRuleTargets, isSummaryView } from '../../../utils/risk-profiles';
import { PROPOSAL_ORIENTATION } from '../utils/constants';
import { splitArrayInGroups } from '../../../utils/objects';

// Components
import HoldingIcon, { Props as HoldingIconProps } from '../../../views/components/common/HoldingIcon';

// Types
import { RiskProfileRule, RiskProfileRuleData } from '../../../types/risk-profiles';
import RiskProfileItem from '@/types/risk-profile-item';
import ComparisonItem from '@/types/comparison-item';
import ProposalOrientation from '@/types/proposal-orientation';
import Locale from '@/types/locale';
import User from '@/types/user';

class ProposalRiskProfilesSectionBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  proposalRiskProfile?: RiskProfileItem;

  proposalItems: ComparisonItem[];

  orientation: ProposalOrientation;

  locale: Locale;

  user: User;

  section: ProposalNS.ProposalTemplateSectionElement;

  constructor(
    section: ProposalNS.ProposalTemplateSectionElement,
    proposalRiskProfile: RiskProfileItem | undefined = undefined,
    proposalItems: ComparisonItem[],
    orientation: ProposalOrientation,
    locale: Locale,
    user: User,
  ) {
    this.section = section;
    this.proposalRiskProfile = proposalRiskProfile;
    this.proposalItems = proposalItems;
    this.orientation = orientation;
    this.locale = locale;
    this.user = user;
  }

  build = () => {
    if (this.proposalRiskProfile) {
      this.buildComparisonRiskProfileSections();
    } else if (this.proposalItems.some(item => item.riskProfile != null)) {
      this.buildPortfolioRiskProfileSections();
    }
  };

  // eslint-disable-next-line class-methods-use-this
  buildItemsComparedToLabel(): ProposalNS.ProposalTemplateElement {
    return {
      type: 'DATA',
      dataView: {
        type: 'TEXT',
        className: 'items-compared-to no-border',
      },
      DAO: {
        type: 'TEXT',
        localizedTextDAOOptions: {
          texts: [
            {
              className: 'items-compared-title',
              text: {
                'en-CA': 'Items compared to target: ',
                'fr-CA': 'Comparaison des éléments avec la cible : ',
              },
            },
          ],
          pageOverflowSuffix: {
            text: {
              'en-CA': ' (Continued)',
              'fr-CA': ' (suite)',
            },
          },
        },
      },
    } as ProposalNS.ProposalTemplateElement;
  }

  buildRiskProfileDescription(profile: RiskProfileItem): ProposalNS.ProposalTemplateElement {
    const profileTargetElements: ProposalNS.ProposalTemplateElement[] = [];

    for (const rule of profile.rules) {
      const targets = getRiskProfileRuleTargets(rule, this.locale);

      for (const target of targets) {
        profileTargetElements.push({
          type: 'DATA',
          dataView: {
            type: 'TEXT',
            className: `risk-profile-min-max no-border ${isSummaryView(profile) ? 'summaryView' : ''}`,
          },
          DAO: {
            type: 'TEXT',
            localizedTextDAOOptions: {
              texts: [
                {
                  text: {
                    'en-CA': target.value,
                    'fr-CA': target.value,
                  },
                },
              ],
            },
          },
        } as ProposalNS.ProposalTemplateElement);

        profileTargetElements.push({
          type: 'DATA',
          dataView: {
            type: 'TEXT',
            className: 'risk-profile-alloc-label no-border',
          },
          DAO: {
            type: 'TEXT',
            localizedTextDAOOptions: {
              texts: [
                {
                  text: {
                    'en-CA': target.label,
                    'fr-CA': target.label,
                  },
                },
              ],
            },
          },
        } as ProposalNS.ProposalTemplateElement);
      }
    }

    return {
      type: 'LAYOUT',
      layout: {
        type: 'GRID',
        columns: 2,
        className: 'risk-profile-details',
      },
      buildRules: [
        {
          type: 'PAGE_OVERFLOW_INDEX',
          firstPageOnly: true,
        },
      ],
      elements: [
        {
          type: 'LAYOUT',
          layout: {
            type: 'GRID',
            columns: 1,
          },
          elements: [
            {
              type: 'DATA',
              dataView: {
                type: 'TEXT',
                className: 'risk-profile-title no-border',
              },
              DAO: {
                type: 'TEXT',
                localizedTextDAOOptions: {
                  texts: [
                    {
                      text: {
                        'en-CA': 'Your investment profile is: ',
                        'fr-CA': "Votre profil d'investisseur est : ",
                      },
                      className: 'risk-profile-label',
                    },
                    {
                      text: {
                        'en-CA': formatLocalizedString(profile.title, this.locale),
                        'fr-CA': formatLocalizedString(profile.title, this.locale),
                      },
                      className: 'risk-profile-name',
                    },
                  ],
                },
              },
            },
            {
              type: 'DATA',
              dataView: {
                type: 'TEXT',
                className: 'risk-profile-description no-border',
              },
              DAO: {
                type: 'TEXT',
                localizedTextDAOOptions: {
                  texts: [
                    {
                      text: {
                        'en-CA': formatLocalizedString(profile.description, this.locale),
                        'fr-CA': formatLocalizedString(profile.description, this.locale),
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        {
          type: 'LAYOUT',
          layout: {
            type: 'GRID',
            columns: 1,
            className: 'risk-profile-allocation',
          },
          elements: profileTargetElements,
        },
      ],
    } as ProposalNS.ProposalTemplateElement;
  }

  buildRiskProfileRuleElement(item: ComparisonItem, rules: RiskProfileRule[], daoConfig: ProposalNS.RiskProfileConfig, index: number, size: number) {
    const displayRules = rules.reduce(
      (acc, rule, index) => {
        const data: RiskProfileRuleData[] = getRiskProfileRuleData(rule, item, this.locale, this.user);
        if (data.length !== 0) {
          acc.push({ rule, index, data });
        }
        return acc;
      },
      [] as { rule: RiskProfileRule; index: number; data: RiskProfileRuleData[] }[],
    );

    return displayRules.map(displayRule => {
      const { rule } = displayRule;
      const ruleIndex = displayRule.index;
      return {
        type: 'LAYOUT',
        layout: {
          type: 'GRID',
          columns: 2,
          className: 'risk-profile-donut-item',
        },
        elements: [
          {
            type: 'DATA',
            dataView: {
              type: 'TEXT',
              className: 'risk-rule-title no-border',
            },
            DAO: {
              type: 'TEXT',
              localizedTextDAOOptions: {
                texts: [
                  {
                    text: {
                      'en-CA': formatMessageWithLocale(`RiskProfile.Rules.Titles.${rule.ruleType}`, this.locale),
                      'fr-CA': formatMessageWithLocale(`RiskProfile.Rules.Titles.${rule.ruleType}`, this.locale),
                    },
                  },
                ],
              },
            },
          },
          {
            type: 'DATA',
            dataView: {
              type: 'DONUT',
              showLegend: false,
              showHeader: true,
              configOptions: {
                chart: {
                  height: size,
                  width: size,
                },
                series: [
                  {
                    type: 'pie',
                    size: daoConfig.sizes.actual.outer,
                    innerSize: daoConfig.sizes.actual.inner,
                  },
                  {
                    type: 'pie',
                    size: daoConfig.sizes.target.outer,
                    innerSize: daoConfig.sizes.target.inner,
                  },
                ],
                plotOptions: {
                  pie: {
                    borderWidth: daoConfig.borderWidth,
                  },
                  series: {
                    enableMouseTracking: false,
                    animation: false,
                    label: {
                      enabled: false,
                    },
                    dataLabels: {
                      enabled: false,
                    },
                  },
                  legend: {
                    enabled: false,
                  },
                  tooltip: {
                    enabled: false,
                  },
                },
              },
            },
            DAO: {
              ...daoConfig,
              type: 'RISK_PROFILE',
              ruleIndex,
              filterItemsByIndexDAOOptions: {
                selection: [index],
              },
            },
          },
          {
            type: 'DATA',
            dataView: {
              type: 'TABLE',
            },
            DAO: {
              ...daoConfig,
              type: 'RISK_PROFILE',
              ruleIndex,
              filterItemsByIndexDAOOptions: {
                selection: [index],
              },
            },
          },
        ],
      };
    });
  }

  buildItemsProfiles(
    profile: RiskProfileItem,
    items: ComparisonItem[],
    daoConfig: ProposalNS.RiskProfileConfig,
    filterItemsByIndexDAOOptions?: ProposalNS.FilterItemsByIndexDAOOptions,
  ) {
    if (isSummaryView(profile)) {
      return {
        type: 'DATA',
        dataView: {
          type: 'TABLE',
          className: 'FULL_PROFILE',
        },
        DAO: {
          type: 'RISK_PROFILE',
          summaryView: true,
          filterItemsByIndexDAOOptions,
        },
      };
    }

    const getItemTitle = (item: ComparisonItem) => {
      const holdingIcon: string = ReactDOMServer.renderToString(
        React.createElement<HoldingIconProps>(HoldingIcon, {
          type: item.type,
          color: item.color,
        }),
      );

      const formattedItemName = `
        <div class="risk-profile-item-name">
          ${holdingIcon}
          <span>${formatLocalizedString(item.name, this.locale)}</span>
        </div>
      `;

      return {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Html,
          className: 'no-border',
        },
        DAO: {
          type: ProposalNS.DAOType.HTMLContent,
          htmlContentDAOOptions: {
            html: [
              {
                content: {
                  'en-CA': formattedItemName,
                  'fr-CA': formattedItemName,
                },
              },
            ],
          },
        },
      };
    };

    return {
      type: 'LAYOUT',
      layout: {
        type: 'GRID',
        columns: 2,
        className: 'risk-profile-donut-grid',
      },
      elements: items.map(item => {
        const index = this.proposalItems.findIndex(proposalItem => item._id === proposalItem._id);
        return {
          type: 'LAYOUT',
          layout: {
            type: 'GRID',
            columns: 1,
            className: 'risk-profile-donut-grid-item',
          },
          elements: [getItemTitle(item), ...this.buildRiskProfileRuleElement(item, profile.rules, daoConfig, index, 120)],
        };
      }),
    };
  }

  getRiskProfileElement() {
    const riskProfileElement = this.section.layout.elements.find(
      element => element.type === ProposalNS.ProposalTemplateElementType.Data && element.DAO.type === ProposalNS.DAOType.RiskProfile,
    ) as ProposalNS.ProposalTemplateDataElement | undefined;

    return riskProfileElement;
  }

  buildComparisonRiskProfileSections() {
    const profile = this.proposalRiskProfile;
    if (!profile) {
      return;
    }

    const riskProfileElement = this.getRiskProfileElement();
    const daoConfig = riskProfileElement && (riskProfileElement.DAO as ProposalNS.RiskProfileConfig);
    if (!daoConfig) {
      // Should never happen - safeguard.
      return;
    }

    const pages = isSummaryView(profile)
      ? [this.proposalItems]
      : splitArrayInGroups(this.proposalItems, this.orientation === PROPOSAL_ORIENTATION.LANDSCAPE ? 2 : 4);

    this.newSections = pages.map(
      (items, pageIndex) =>
        ({
          type: ProposalNS.ProposalTemplateElementType.Section,
          key: `RISK_PROFILE CUSTOM_RISK_PROFILE RISK_PROFILE_${pageIndex}`,
          forced: true,
          pageFraction: {
            Portrait: 1,
            Landscape: 1,
          },
          label: {
            'en-CA': 'Risk Profile',
            'fr-CA': 'Profil de risque',
          },
          sidebarControl: ProposalNS.ProposalSidebarControl.Hide,
          showItemNamesInDisclaimers: true,
          buildRules: [
            {
              type: 'RISK_PROFILE',
            },
            {
              type: 'PERMISSION',
              permissions: ['riskProfile', 'riskQuestionnaire'],
              operator: 'OR',
            },
          ],
          layout: {
            type: 'LAYOUT',
            layout: {
              type: 'GRID',
              columns: 1,
              className: 'risk-profile-grid comparison-risk-profile',
            },
            elements: [this.buildRiskProfileDescription(profile), this.buildItemsComparedToLabel(), this.buildItemsProfiles(profile, items, daoConfig)],
          },
          settings: this.section.settings,
        }) as ProposalNS.ProposalTemplateSectionElement,
    );
  }

  buildPortfolioRiskProfileSections() {
    const profileItems = this.proposalItems.filter(item => item.riskProfile != null);
    if (profileItems.length === 0) {
      return;
    }

    const riskProfileElement = this.getRiskProfileElement();
    const daoConfig = riskProfileElement && (riskProfileElement.DAO as ProposalNS.RiskProfileConfig);
    if (!daoConfig) {
      // Should never happen - safeguard.
      return;
    }

    this.proposalItems.forEach((item, index) => {
      const { riskProfile } = item;
      if (riskProfile == null) {
        return;
      }

      this.newSections.push({
        type: 'SECTION',
        key: `RISK_PROFILE CUSTOM_RISK_PROFILE RISK_PROFILE_${index}`,
        forced: true,
        buildRules: [
          {
            type: 'RISK_PROFILE',
          },
          {
            type: 'PERMISSION',
            permissions: ['riskProfile', 'riskQuestionnaire'],
            operator: 'OR',
          },
        ],
        pageFraction: {
          Portrait: isSummaryView(riskProfile) ? 1.0 : 0.6,
          Landscape: 1.0,
        },
        label: {
          'en-CA': 'Risk Profile',
          'fr-CA': 'Profil de risque',
        },
        sidebarControl: 'CHECKBOX',
        showTitle: false,
        showItemNamesInDisclaimers: true,
        settings: this.section.settings,
        layout: {
          type: 'LAYOUT',
          layout: {
            type: 'GRID',
            columns: 1,
          },
          elements: [
            {
              type: 'LAYOUT',
              layout: {
                type: 'GRID',
                columns: 1,
              },
              elements: [
                {
                  type: 'DATA',
                  dataView: {
                    type: 'HTML',
                    className: 'proposal-section-header',
                  },
                  DAO: {
                    type: 'HTML_CONTENT',
                    htmlContentDAOOptions: {
                      html: [
                        {
                          className: 'proposal-section-header-content',
                          content: {
                            'en-CA': `<h2>Risk Profile: ${formatLocalizedString(item.name, this.locale)}</h2>`,
                            'fr-CA': `<h2>Profil de risque: ${formatLocalizedString(item.name, this.locale)}</h2>`,
                          },
                        },
                      ],
                    },
                  },
                },
                {
                  type: 'LAYOUT',
                  layout: {
                    type: 'GRID',
                    columns: 1,
                    className: 'risk-profile-grid comparison-risk-profile',
                  },
                  elements: !riskProfile
                    ? []
                    : [
                        this.buildRiskProfileDescription(riskProfile),
                        this.buildItemsComparedToLabel(),
                        this.buildItemsProfiles(riskProfile, [item], daoConfig, { selection: [index] }),
                      ],
                },
              ],
            },
          ],
        },
      } as ProposalNS.ProposalTemplateSectionElement);
    });
  }
}

export const buildRiskProfileSections = (
  section: ProposalNS.ProposalTemplateSectionElement,
  proposalRiskProfile: RiskProfileItem | undefined = undefined,
  proposalItems: ComparisonItem[],
  orientation: ProposalOrientation,
  locale: Locale,
  user: User,
): ProposalNS.ProposalTemplateSectionElement[] => {
  if (proposalRiskProfile == null && !proposalItems.some(item => item.riskProfile != null)) {
    return [];
  }

  const builder: ProposalRiskProfilesSectionBuilder = new ProposalRiskProfilesSectionBuilder(
    section,
    proposalRiskProfile,
    proposalItems,
    orientation,
    locale,
    user,
  );
  builder.build();
  return builder.newSections;
};
